import {
  infiniteQueryOptions,
  InfiniteData,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback } from "react";
import {
  getNotifications,
  getUnreadNotificationCount,
  markAsRead,
  markAsUnread,
  Notification,
} from "../api/notification";
import { PaginatedResponse } from "../types";

export const notificationKeys = {
  all: ["notifications"] as const,
  list: (importantOnly?: boolean, clientId?: string) =>
    [...notificationKeys.all, { importantOnly, clientId }] as const,
  unreadCount: (importantOnly?: boolean, clientId?: string) =>
    [
      ...notificationKeys.all,
      "unread-count",
      { importantOnly, clientId },
    ] as const,
};

export const notificationsQueryOptions = (variables?: {
  importantOnly?: boolean;
  clientId?: number;
  minDate?: string;
}) =>
  infiniteQueryOptions({
    queryKey: notificationKeys.list(variables?.importantOnly),
    queryFn: (context) =>
      getNotifications({
        page: context.pageParam,
        pageSize: 20,
        importantOnly: variables?.importantOnly,
        clientId: variables?.clientId,
        minDate: variables?.minDate,
      }),
    getNextPageParam: (lastPage, pages) => {
      return lastPage.results?.length === 20
        ? (pages.length ?? 0) + 1
        : undefined;
    },
    initialPageParam: 1,
  });

export const unreadNotificationCountQueryOptions = () =>
  queryOptions({
    queryKey: notificationKeys.unreadCount(),
    queryFn: () => getUnreadNotificationCount(),
    refetchInterval: 30000,
    refetchIntervalInBackground: true,
  });

export const unreadImportantNotificationsCountQueryOptions = () =>
  queryOptions({
    queryKey: notificationKeys.unreadCount(true),
    queryFn: () => getUnreadNotificationCount(true),
  });

export function useNotifications(variables?: {
  importantOnly?: boolean;
  clientId?: number;
  minDate?: string;
}) {
  return useInfiniteQuery(notificationsQueryOptions(variables));
}

export function useUnreadNotificationCount() {
  return useQuery(unreadNotificationCountQueryOptions());
}

export function useUnreadImportNotificationsCount() {
  return useQuery(unreadImportantNotificationsCountQueryOptions());
}

export function useInvalidateNotificationsCount() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: notificationKeys.unreadCount(),
    });
    queryClient.invalidateQueries({
      queryKey: notificationKeys.unreadCount(true),
    });
  }, [queryClient]);
}

export function useMarkAsRead() {
  const invalidateNotificationsCount = useInvalidateNotificationsCount();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { id: number }) => markAsRead(params),
    onSuccess: (_, variables) => {
      invalidateNotificationsCount();
      queryClient.setQueriesData<InfiniteData<PaginatedResponse<Notification>>>(
        { queryKey: notificationKeys.all },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages?.map((page) => ({
              ...page,
              results: page.results.map((result) =>
                result.id === variables.id
                  ? { ...result, read_at: new Date().toISOString() }
                  : result
              ),
            })),
          };
        }
      );
    },
  });
}

export function useMarkAsUnread() {
  const invalidateNotificationsCount = useInvalidateNotificationsCount();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markAsUnread,
    onSuccess: (_, variables) => {
      invalidateNotificationsCount();
      queryClient.setQueriesData<InfiniteData<PaginatedResponse<Notification>>>(
        { queryKey: notificationKeys.all },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages?.map((page) => ({
              ...page,
              results: page.results.map((result) =>
                result.id === variables.id
                  ? { ...result, read_at: null }
                  : result
              ),
            })),
          };
        }
      );
    },
  });
}

export function useMarkAllAsRead() {
  const invalidateNotificationsCount = useInvalidateNotificationsCount();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (importantOnly?: boolean) => markAsRead({ importantOnly }),
    onSuccess: () => {
      invalidateNotificationsCount();
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}
